import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  ArchiveRestore,
  ExternalLink,
  Pause,
  Pencil,
  Play,
  Plus,
  Users,
} from "lucide-react";
import {
  ApiError,
  createClub,
  listClubs,
  setClubStatus,
  updateClub,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/Button";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import {
  AdminIconButton,
  AdminIconLink,
} from "@/components/console/AdminIconButton";
import { ClubAdminsPanel } from "@/features/super-admin/components/ClubAdminsPanel";
import { apexDomain } from "@/lib/hostKind";
import { AdminMetricTile } from "@/components/console/AdminMetricTile";
import { AdminPageHeader } from "@/components/console/AdminPageHeader";
import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
} from "@/features/collections/CollectionControls";
import type { AdminClubRow } from "@/lib/types";

function formatErr(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong.";
}

const CLUBS_KEY = unscopedKey("super-admin-clubs");

// The list filter: "default" hides archived (retired clubs stay out of the
// working list); the rest are exact statuses.
type ClubsFilter = "default" | "active" | "suspended" | "archived" | "all";

const FILTER_CHIPS: Array<{ value: ClubsFilter; label: string }> = [
  { value: "default", label: "Current" },
  { value: "active", label: "Active" },
  { value: "suspended", label: "Suspended" },
  { value: "archived", label: "Archived" },
  { value: "all", label: "All" },
];

export function ClubsPage() {
  const [composerOpen, setComposerOpen] = useState(false);
  const [filter, setFilter] = useState<ClubsFilter>("default");
  const clubsQuery = useQuery({
    // Same base key as the unfiltered list so existing prefix
    // invalidations (admin/quota mutations) refresh every filter view.
    queryKey: unscopedKey("super-admin-clubs", { filter: "all" }),
    queryFn: () => listClubs("all"),
    staleTime: 10_000,
  });

  const clubs = clubsQuery.data ?? [];
  const visibleClubs = clubs.filter((club) =>
    filter === "default"
      ? club.status !== "archived"
      : filter === "all" || club.status === filter,
  );
  const collection = useCollection(visibleClubs, {
    scope: `platform-clubs:${filter}`,
    initialPageSize: 10,
    text: (club) => `${club.name} ${club.slug} ${club.subdomain}`,
  });
  return (
    <div data-testid="super-admin-clubs" className="flex flex-col gap-3">
      <AdminPageHeader
        eyebrow="Platform"
        title="Clubs"
        description="Every club on the platform, with tenant access and lifecycle controls."
        actions={
          <Button
            variant="primary"
            size="sm"
            onClick={() => setComposerOpen(true)}
            data-testid="super-admin-create-club"
          >
            <Plus aria-hidden />
            New club
          </Button>
        }
      />
      {clubsQuery.data ? (
        <div className="platform-kpi-grid">
          <AdminMetricTile
            label="Clubs"
            value={String(clubs.length)}
            sub="Across the platform"
          />
          <AdminMetricTile
            label="Active"
            value={String(clubs.filter((c) => c.status === "active").length)}
            sub="Available to members"
          />
          <AdminMetricTile
            label="Suspended"
            value={String(clubs.filter((c) => c.status === "suspended").length)}
            sub="Operator attention"
          />
          <AdminMetricTile
            label="People"
            value={String(clubs.reduce((sum, c) => sum + c.member_count, 0))}
            sub="Club memberships"
          />
        </div>
      ) : null}
      <section className="platform-panel" aria-label="Club directory">
        <div className="platform-panel-head">
          <div>
            <h3>Club directory</h3>
            <p>Each club has its own subdomain, squads and members.</p>
          </div>
        </div>
        <div
          data-testid="super-admin-clubs-filter"
          className="platform-filter-tabs"
          role="group"
          aria-label="Filter clubs by status"
        >
          {FILTER_CHIPS.map((chip) => (
            <button
              key={chip.value}
              type="button"
              onClick={() => setFilter(chip.value)}
              data-testid={`super-admin-clubs-filter-${chip.value}`}
              data-active={filter === chip.value}
              aria-pressed={filter === chip.value}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <CollectionToolbar
          collection={collection}
          label="clubs"
          searchPlaceholder="Club name or subdomain"
        />
        {clubsQuery.isError ? (
          <div role="alert">
            <p className="text-destructive-text">
              {formatErr(clubsQuery.error)}
            </p>
            <Button variant="ghost" onClick={() => void clubsQuery.refetch()}>
              Retry
            </Button>
          </div>
        ) : clubsQuery.isPending ? (
          <p role="status">Loading clubs…</p>
        ) : collection.items.length === 0 ? (
          <p
            data-testid="super-admin-clubs-empty"
            className="py-6 text-center text-ink-muted"
          >
            {collection.query
              ? "No clubs match your search."
              : "No clubs in this view."}
          </p>
        ) : (
          <div className="platform-table-wrap">
            <table className="platform-table">
              <thead>
                <tr>
                  {[
                    "Club",
                    "Teams",
                    "Members",
                    "Admins",
                    "Status",
                    "Actions",
                  ].map((h) => (
                    <th key={h}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {collection.items.map((club) => (
                  <ClubRow key={club.id} club={club} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        <CollectionPagination collection={collection} label="clubs" />
      </section>
      {composerOpen ? (
        <ClubComposer onClose={() => setComposerOpen(false)} />
      ) : null}
    </div>
  );
}

const STATUS_TOAST: Record<string, string> = {
  active: "reactivated",
  suspended: "suspended",
  archived: "archived",
};

function ClubRow({ club }: { club: AdminClubRow }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [adminsOpen, setAdminsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const suspended = club.status === "suspended";
  const archived = club.status === "archived";
  const mut = useMutation({
    mutationFn: (next: "active" | "suspended" | "archived") =>
      setClubStatus(club.id, next),
    onSuccess: (_club, next) => {
      toast.success(`${club.name} ${STATUS_TOAST[next]}`);
      queryClient.invalidateQueries({ queryKey: CLUBS_KEY });
    },
    onError: (err) =>
      toast.error("Couldn't update club", { description: formatErr(err) }),
  });

  return (
    <tr
      data-testid="super-admin-club-row"
      data-club-id={club.id}
      data-status={club.status}
      className="border-b border-rule last:border-0"
    >
      <td data-label="Club">
        <div className="platform-cell-main">
          <Link
            to="/super-admin/clubs/$clubId"
            params={{ clubId: club.id }}
            data-testid="super-admin-club-open-detail"
          >
            {club.name}
          </Link>
          <span>
            {club.subdomain}.{apexDomain()}
          </span>
        </div>
      </td>
      <td data-label="Teams" data-testid="super-admin-club-counts">
        {club.team_count}
      </td>
      <td data-label="Members">{club.member_count}</td>
      <td data-label="Admins">{club.admin_count}</td>
      <td data-label="Status">
        <span className="platform-status" data-status={club.status}>
          {archived ? "Archived" : suspended ? "Suspended" : "Active"}
        </span>
      </td>
      <td data-label="Actions">
        <div className="platform-row-actions">
          {club.status === "active" ? (
            // Step INTO the club: its admin console lives on the club's own
            // subdomain (there is no host club on this apex console to scope
            // an edit to). The shared *.tktic.com session carries over, and
            // the super-admin override grants club_admin capabilities there.
            // Plain anchor (new host = full document load) in a new tab so
            // the operator keeps this console. A suspended club's host 404s,
            // so the link hides with it.
            <AdminIconLink
              icon={ExternalLink}
              label={`Open ${club.name}'s admin console`}
              href={`https://${club.subdomain}.${apexDomain()}/admin/settings`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="super-admin-club-open"
            />
          ) : null}
          <AdminIconButton
            visibleLabel
            icon={Users}
            label="Admins"
            onClick={() => setAdminsOpen(true)}
            data-testid="super-admin-club-admins"
          />
          <AdminIconButton
            visibleLabel
            icon={Pencil}
            label="Edit"
            onClick={() => setEditOpen(true)}
            data-testid="super-admin-club-edit"
          />
          {!archived ? (
            <AdminIconButton
              visibleLabel
              icon={suspended ? Play : Pause}
              label={suspended ? "Reactivate club" : "Suspend club"}
              onClick={() => mut.mutate(suspended ? "active" : "suspended")}
              disabled={mut.isPending}
              data-testid="super-admin-club-toggle"
              tone={suspended ? "accent" : "destructive"}
            />
          ) : null}
          {suspended ? (
            // Archive only FROM suspended: suspend is the "are we sure" stage;
            // archive retires the club (hidden from the default list, host off,
            // invites refused) and is the only state hard-delete accepts.
            <AdminIconButton
              visibleLabel
              icon={Archive}
              label="Archive club"
              onClick={() => mut.mutate("archived")}
              disabled={mut.isPending}
              data-testid="super-admin-club-archive"
              tone="destructive"
            />
          ) : null}
          {archived ? (
            <AdminIconButton
              visibleLabel
              icon={ArchiveRestore}
              label="Restore club"
              onClick={() => mut.mutate("active")}
              disabled={mut.isPending}
              data-testid="super-admin-club-restore"
              tone="accent"
            />
          ) : null}
        </div>
      </td>
      {adminsOpen ? (
        <ConfirmDialog
          wide
          title={`Club admins — ${club.name}`}
          confirmLabel="Done"
          onConfirm={() => setAdminsOpen(false)}
          onClose={() => setAdminsOpen(false)}
          message={<ClubAdminsPanel club={club} />}
        />
      ) : null}
      {editOpen ? (
        <EditClubDialog club={club} onClose={() => setEditOpen(false)} />
      ) : null}
    </tr>
  );
}

const DIALOG_FIELD =
  "rounded-control border border-rule bg-field px-2 py-1.5 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const DIALOG_LABEL =
  "text-[0.6rem] font-display uppercase tracking-wider text-ink-muted";

// Exported for reuse on the club detail page's identity card.
export function EditClubDialog({
  club,
  onClose,
}: {
  club: AdminClubRow;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState(club.name);
  const [slug, setSlug] = useState(club.slug);
  const [subdomain, setSubdomain] = useState(club.subdomain);
  const [error, setError] = useState<string | null>(null);
  const subdomainChanged = subdomain.trim() !== club.subdomain;

  const mut = useMutation({
    mutationFn: () =>
      updateClub(club.id, {
        name: name.trim() !== club.name ? name.trim() : undefined,
        slug: slug.trim() !== club.slug ? slug.trim() : undefined,
        subdomain: subdomainChanged ? subdomain.trim() : undefined,
      }),
    onSuccess: () => {
      toast.success("Club updated");
      queryClient.invalidateQueries({ queryKey: CLUBS_KEY });
      onClose();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't update club", { description: formatErr(err) });
    },
  });

  return (
    <ConfirmDialog
      title={`Edit ${club.name}`}
      confirmLabel={mut.isPending ? "Saving…" : "Save changes"}
      confirmDisabled={
        mut.isPending || !name.trim() || !slug.trim() || !subdomain.trim()
      }
      onConfirm={() => mut.mutate()}
      onClose={onClose}
      message={
        <div className="platform-form-grid">
          <label className="flex flex-col gap-1">
            <span className={DIALOG_LABEL}>Name</span>
            <input
              className={DIALOG_FIELD}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="super-admin-edit-name"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={DIALOG_LABEL}>Slug</span>
            <input
              className={DIALOG_FIELD}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              data-testid="super-admin-edit-slug"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={DIALOG_LABEL}>Subdomain</span>
            <input
              className={DIALOG_FIELD}
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              data-testid="super-admin-edit-subdomain"
            />
          </label>
          {subdomainChanged ? (
            <p
              data-testid="super-admin-edit-subdomain-warning"
              className="rounded-control border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-[0.65rem] text-destructive-text"
            >
              Changing the subdomain moves the club to{" "}
              <span className="font-mono">
                {subdomain.trim()}.{apexDomain()}
              </span>
              . Existing links break, and the new subdomain needs its DNS /
              certificate in place before it will load. The old subdomain is
              released — another club can claim it, and you can move back to it
              only while it stays free.
            </p>
          ) : null}
          {error ? (
            <p className="text-destructive-text font-mono text-xs">{error}</p>
          ) : null}
        </div>
      }
    />
  );
}

function ClubComposer({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createClub({ name, slug, subdomain: subdomain || undefined }),
    onSuccess: () => {
      toast.success("Club created");
      queryClient.invalidateQueries({ queryKey: CLUBS_KEY });
      onClose();
    },
    onError: (err) => {
      setError(formatErr(err));
      toast.error("Couldn't create club", { description: formatErr(err) });
    },
  });

  const field =
    "rounded-control border border-rule bg-field px-2 py-1.5 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
  const labelText =
    "text-[0.6rem] font-display uppercase tracking-wider text-ink-muted";

  return (
    <ConfirmDialog
      title="New club"
      confirmLabel={mut.isPending ? "Creating…" : "Create club"}
      confirmDisabled={mut.isPending || !name.trim() || !slug.trim()}
      onConfirm={() => mut.mutate()}
      onClose={onClose}
      message={
        <div className="platform-form-grid">
          <label className="flex flex-col gap-1">
            <span className={labelText}>Name</span>
            <input
              className={field}
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="super-admin-club-name"
              autoFocus
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelText}>Slug</span>
            <input
              className={field}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="riverside"
              data-testid="super-admin-club-slug"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className={labelText}>Subdomain (defaults to slug)</span>
            <input
              className={field}
              value={subdomain}
              onChange={(e) => setSubdomain(e.target.value)}
              placeholder="riverside"
              data-testid="super-admin-club-subdomain"
            />
          </label>
          {error ? (
            <p className="text-destructive-text font-mono text-xs">{error}</p>
          ) : null}
        </div>
      }
    />
  );
}
